import React from 'react';
import { CalendarCheck, FileText, ClipboardList, Stethoscope, Microscope, HeartHandshake, ShieldCheck } from 'lucide-react';
export function PatientInformation() {
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-704">
      {/* Hero Band */}
      <div className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-705">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-706">
          <span className="text-brand-gold font-semibold tracking-wider text-sm uppercase mb-4 block" data-id="element-707">
            Patient Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-708">
            Patient Information
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-709">
            Everything you need to know before your visit to Maisha Medical
            Center to ensure a smooth and comfortable experience.
          </p>
        </div>
      </div>

      <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-id="element-710">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-711">
          {/* Left Column: Info Cards */}
          <div className="lg:col-span-2 space-y-8" data-id="element-712">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-713">
              <div className="flex items-center gap-4 mb-6" data-id="element-714">
                <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-navy" data-id="element-715">
                  <CalendarCheck className="w-6 h-6" data-id="element-716" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-brand-navy" data-id="element-717">
                  Appointments
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-718">
                Patients are encouraged to schedule appointments in advance to
                minimize wait times and ensure our specialists are available.
                However, walk-ins may be accommodated depending on availability
                and the urgency of the medical condition.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-719">
              <div className="flex items-center gap-4 mb-6" data-id="element-720">
                <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-navy" data-id="element-721">
                  <ShieldCheck className="w-6 h-6" data-id="element-722" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-brand-navy" data-id="element-723">
                  Medical Records
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-724">
                All patient records are handled with the strictest
                confidentiality in compliance with healthcare regulations. Your
                medical history is securely stored and only accessible to
                authorized medical personnel involved in your care.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-725">
              <div className="flex items-center gap-4 mb-6" data-id="element-726">
                <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-navy" data-id="element-727">
                  <ClipboardList className="w-6 h-6" data-id="element-728" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-brand-navy" data-id="element-729">
                  Preparing For Your Visit
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6" data-id="element-730">
                To help us provide you with the best possible care, please
                ensure you bring the following items to your appointment:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-731">
                {['National Identification or Passport', 'Referral letter (if applicable)', 'Previous medical history/records', 'List of current medications', 'Medical aid/Insurance information'].map((item, idx) => <li key={idx} className="flex items-start gap-3 bg-brand-lighter p-4 rounded-xl" data-id="element-732">
                    <div className="w-2 h-2 rounded-full bg-brand-gold mt-2 shrink-0" data-id="element-733" />
                    <span className="text-slate-700 font-medium text-sm" data-id="element-734">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Right Column: What to Expect Timeline */}
          <div className="lg:col-span-1" data-id="element-735">
            <div className="bg-brand-navy p-8 rounded-2xl shadow-soft-lg text-white sticky top-28" data-id="element-736">
              <h2 className="text-2xl font-heading font-bold text-white mb-8" data-id="element-737">
                What To Expect
              </h2>

              <div className="space-y-6" data-id="element-738">
                <div className="flex items-center gap-5" data-id="element-739">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-red text-white shrink-0 shadow-md" data-id="element-740">
                    <FileText className="w-5 h-5" data-id="element-741" />
                  </div>
                  <div data-id="element-742">
                    <h3 className="font-heading font-bold text-lg mb-1" data-id="element-743">
                      1. Reception
                    </h3>
                    <p className="text-slate-300 text-sm" data-id="element-744">
                      Registration, verifying details, and initial vitals check.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-id="element-745">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-red text-white shrink-0 shadow-md" data-id="element-746">
                    <Stethoscope className="w-5 h-5" data-id="element-747" />
                  </div>
                  <div data-id="element-748">
                    <h3 className="font-heading font-bold text-lg mb-1" data-id="element-749">
                      2. Consultation
                    </h3>
                    <p className="text-slate-300 text-sm" data-id="element-750">
                      Detailed discussion of symptoms with our medical
                      professionals.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-id="element-751">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-red text-white shrink-0 shadow-md" data-id="element-752">
                    <Microscope className="w-5 h-5" data-id="element-753" />
                  </div>
                  <div data-id="element-754">
                    <h3 className="font-heading font-bold text-lg mb-1" data-id="element-755">
                      3. Diagnosis
                    </h3>
                    <p className="text-slate-300 text-sm" data-id="element-756">
                      Laboratory tests or imaging if required for accurate
                      assessment.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-id="element-757">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-red text-white shrink-0 shadow-md" data-id="element-758">
                    <ClipboardList className="w-5 h-5" data-id="element-759" />
                  </div>
                  <div data-id="element-760">
                    <h3 className="font-heading font-bold text-lg mb-1" data-id="element-761">
                      4. Treatment Plan
                    </h3>
                    <p className="text-slate-300 text-sm" data-id="element-762">
                      Prescriptions, procedures, or specialist referrals
                      provided.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-5" data-id="element-763">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-red text-white shrink-0 shadow-md" data-id="element-764">
                    <HeartHandshake className="w-5 h-5" data-id="element-765" />
                  </div>
                  <div data-id="element-766">
                    <h3 className="font-heading font-bold text-lg mb-1" data-id="element-767">
                      5. Follow-Up
                    </h3>
                    <p className="text-slate-300 text-sm" data-id="element-768">
                      Scheduling your next visit to monitor recovery progress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}